import {
  chipIdentity,
  mergeOperationLogs,
  operationLogEntry,
  type ChipDto,
  type EventStateDto,
  type OperationLogEntryDto,
  type ReturnLogDto,
  type ServerMessage,
} from '@pietrochip/shared';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import * as api from '../api/endpoints';
import { OperationLogList } from '../components/OperationLogList';
import { TeamProgress } from '../components/TeamProgress';
import { Notice } from '../components/ui';
import { eventDate } from '../lib/format';
import { useFallbackPoll } from '../realtime/useFallbackPoll';
import { useReconnectingSocket } from '../realtime/useReconnectingSocket';
import { EMPTY_EVENT_STATE, eventReducer, selectChips, selectTeamSummaries } from '../state/eventState';

/**
 * Squadre e registro: il rovescio della schermata di lavoro.
 *
 * La schermata di lavoro risponde a «questo transponder è rientrato?» e guarda
 * gli ultimi secondi. Questa risponde alle due domande che si fanno *dopo* —
 * «quanto manca a questa società?» e «cosa è successo a questo chip?» — e per
 * questo non ha un campo di ricerca, non ha pulsanti e non scrive niente: è una
 * pagina da leggere, mentre qualcun altro continua a lavorare al banco.
 *
 * I chip arrivano dal socket come nella schermata di lavoro, quindi la dashboard
 * è aggiornata **gratis**: è un calcolo su dati che il client ha già. Il
 * registro no — `hello` ne porta solo venticinque — e sta per conto suo, con una
 * rilettura intera a ogni riconnessione.
 */
export function EventOperationsPage() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(eventReducer, EMPTY_EVENT_STATE);
  /** `null` finché il registro non è arrivato: è diverso da «nessuna operazione». */
  const [operations, setOperations] = useState<OperationLogEntryDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Stato dal server ───────────────────────────────────────────────────────

  /**
   * Un'operazione annunciata dal socket entra subito nel registro.
   *
   * L'anagrafica si prende dal chip che viaggia **nello stesso messaggio**, e la
   * riga si costruisce con la stessa funzione che usa il server: così la riga
   * appesa e quella riletta sono identiche per costruzione, non per disciplina.
   */
  const appendLog = useCallback((chip: ChipDto, log: ReturnLogDto | null) => {
    if (!log) return;
    setOperations((current) =>
      mergeOperationLogs(current ?? [], [operationLogEntry(log, chipIdentity(chip))]),
    );
  }, []);

  const onMessage = useCallback(
    (raw: unknown) => {
      const message = raw as ServerMessage | { type: 'pong' };

      switch (message.type) {
        case 'hello':
          // All'apertura e a ogni riconnessione: è la rilettura completa dei
          // chip, senza bisogno di altro.
          dispatch({ type: 'server:state', state: message.state, operators: message.operators });
          setLoadError(null);
          break;

        case 'chip:returned':
        case 'chip:unreturned':
          dispatch({ type: 'server:chip', chip: message.chip, log: message.log });
          appendLog(message.chip, message.log);
          break;

        case 'chip:created':
          dispatch({ type: 'server:chip', chip: message.chip });
          break;

        case 'chips:imported':
        case 'chips:updated':
          dispatch({ type: 'server:chips', chips: message.chips });
          break;

        case 'chip:removed':
          dispatch({ type: 'server:chip-removed', chip: message.chip, log: message.log });
          appendLog(message.chip, message.log);
          break;

        case 'chip:restored':
          dispatch({ type: 'server:chip-restored', chip: message.chip, log: message.log });
          appendLog(message.chip, message.log);
          break;

        case 'event:updated':
          dispatch({ type: 'server:event', event: message.event });
          break;

        case 'presence':
          dispatch({ type: 'server:presence', operators: message.operators });
          break;

        case 'event:deleted':
          navigate('/', { replace: true });
          break;

        default:
          break;
      }
    },
    [appendLog, navigate],
  );

  const { status: socketStatus, generation: socketGeneration } = useReconnectingSocket(
    eventId ? `/api/events/${eventId}/stream` : null,
    onMessage,
  );

  // Lo stato serve dentro la richiamata del ripiego, che è nata in un render
  // precedente: attraverso una ref si legge sempre quello corrente.
  const stateRef = useRef(state);
  stateRef.current = state;
  const socketStatusRef = useRef(socketStatus);
  socketStatusRef.current = socketStatus;

  /**
   * Legge lo stato dei chip e il registro, e li applica.
   *
   * È in due tempi come il ripiego della schermata di lavoro: prima l'impronta —
   * poche decine di byte — e solo se è cambiata si scaricano i chip e il
   * registro. Il registro entra con `mergeOperationLogs`, mai con un replace: una
   * GET partita prima di un'operazione che il socket ci ha già annunciato non
   * deve cancellarla.
   */
  const lastVersionRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;

    const revisionAtStart = stateRef.current.revision;
    const statusAtStart = socketStatusRef.current;

    try {
      let version: string | null = null;

      if (stateRef.current.loaded) {
        const current = await api.getEventVersion(eventId);
        if (current.version === lastVersionRef.current) return;
        version = current.version;
      }

      const [fresh, register]: [EventStateDto, { operations: OperationLogEntryDto[] }] =
        await Promise.all([api.getEventState(eventId), api.fetchEventOperations(eventId)]);

      // Il registro si applica sempre: è un'unione per identificativo, quindi una
      // fotografia vecchia non può togliere niente.
      setOperations((current) => mergeOperationLogs(current ?? [], register.operations));

      // I chip invece si applicano solo se nel frattempo non è arrivato di
      // meglio: applicare una fotografia nata prima riporterebbe indietro la
      // dashboard sotto gli occhi di chi la sta guardando.
      if (
        stateRef.current.revision !== revisionAtStart ||
        socketStatusRef.current !== statusAtStart
      ) {
        return;
      }

      dispatch({ type: 'refresh:state', state: fresh });
      setLoadError(null);

      // L'impronta si registra solo dopo aver applicato: se l'aggiornamento
      // fosse stato scartato, il prossimo giro deve rileggere, non saltare.
      lastVersionRef.current = version ?? (await api.getEventVersion(eventId)).version;
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setLoadError('Questo evento non esiste più.');
      }
      // Un giro perso non è un guasto da mostrare: quello che c'è resta valido.
    }
  }, [eventId]);

  useFallbackPoll({ active: eventId !== '', status: socketStatus, poll: load });

  /**
   * Il registro si rilegge **per intero** a ogni riapertura del socket.
   *
   * È l'unica parte che `hello` non rimanda: i chip arrivano tutti, il registro
   * no — ne porta venticinque. Dopo un buco di rete le operazioni del periodo
   * offline non sono mai state annunciate, e senza questa rilettura sparirebbero
   * dal registro: esattamente le righe che si vanno a cercare quando qualcosa
   * non torna.
   *
   * Qui si rilegge **solo il registro**: i chip li ha appena riportati `hello`,
   * e riscaricarli sarebbe pagare due volte lo stesso stato — su un evento da
   * migliaia di chip, qualche megabyte per niente. La funzione si legge da una
   * ref perché l'effetto deve dipendere dalla sola riapertura.
   */
  const loadRegister = useCallback(async () => {
    if (!eventId) return;

    try {
      const { operations: fresh } = await api.fetchEventOperations(eventId);
      // Unione, non sostituzione: un'operazione annunciata dal socket mentre
      // questa lettura era in volo non deve sparire perché la lettura è nata
      // prima.
      setOperations((current) => mergeOperationLogs(current ?? [], fresh));
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setLoadError('Questo evento non esiste più.');
      }
    }
  }, [eventId]);

  const loadRegisterRef = useRef(loadRegister);
  loadRegisterRef.current = loadRegister;

  useEffect(() => {
    if (socketGeneration === 0) return;
    void loadRegisterRef.current();
  }, [socketGeneration]);

  // Stato azzerato quando si cambia evento: i chip di ieri non devono comparire
  // per un istante in cima a quelli di oggi.
  useEffect(() => {
    lastVersionRef.current = null;
    dispatch({ type: 'reset' });
    setOperations(null);
    setLoadError(null);
  }, [eventId]);

  // ── Dati derivati ──────────────────────────────────────────────────────────

  const chips = useMemo(() => selectChips(state), [state]);
  const teams = useMemo(() => selectTeamSummaries(state), [state]);
  const missingTotal = useMemo(
    () => teams.reduce((sum, team) => sum + team.missing, 0),
    [teams],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="stack">
        <Notice tone="danger">{loadError}</Notice>
        <div className="row">
          <button type="button" className="btn" onClick={() => navigate('/')}>
            Torna agli eventi
          </button>
        </div>
      </div>
    );
  }

  if (!state.loaded || !state.event) {
    return <p className="muted">Caricamento dell’evento…</p>;
  }

  const event = state.event;

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{event.name}</h1>
          <p className="page-head__meta">
            {event.date ? eventDate(event.date) : 'Senza data'}
            {' · '}
            {teams.length === 1 ? '1 squadra' : `${teams.length} squadre`}
            {' · '}
            {missingTotal === 1 ? '1 chip da restituire' : `${missingTotal} chip da restituire`}
          </p>
        </div>

        <div className="page-head__actions">
          <Link className="btn" to={`/eventi/${eventId}`}>
            Torna all’evento
          </Link>
        </div>
      </div>

      {socketStatus !== 'open' && (
        <div className="banner" role="status">
          <span
            className="offline__pulse"
            style={{ width: 14, height: 14, borderWidth: 2 }}
            aria-hidden="true"
          />
          <span>
            {socketStatus === 'connecting'
              ? 'Collegamento in corso…'
              : 'Riconnessione in corso: quello che vedi potrebbe non essere aggiornato.'}
          </span>
        </div>
      )}

      <section className="stack">
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <h2 className="card__title">Chip mancanti per squadra</h2>
          <span className="shell__spacer" />
          <span className="muted" style={{ fontSize: '0.8125rem' }}>
            Apri una squadra per vedere chi manca
          </span>
        </div>
        <TeamProgress chips={chips} />
      </section>

      <section className="stack">
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <h2 className="card__title">Registro delle operazioni</h2>
          <span className="shell__spacer" />
          <span className="muted" style={{ fontSize: '0.8125rem' }}>
            {operations === null
              ? 'Caricamento…'
              : operations.length === 1
                ? '1 operazione'
                : `${operations.length} operazioni`}
          </span>
        </div>
        {operations === null ? (
          <p className="muted">Caricamento del registro…</p>
        ) : (
          <OperationLogList operations={operations} />
        )}
      </section>
    </div>
  );
}
